import { onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue';

import { debounce } from '@sg-core/resources/assets/js/utils';

interface UseTabButtonContext {
  emit: {
    (event: 'tab-resize', element: Element | null | undefined): void;
    (event: 'tab-active', element: HTMLElement | null | undefined): void;
    (event: 'tab-click', id: string, mouseEvent: MouseEvent): void;
    (event: 'mousedown'): void;
  };
}

export function useTabButton({ active, id }: { active: Ref<boolean>; id: string }, ctx: UseTabButtonContext) {
  const buttonRef = ref<HTMLButtonElement | null>(null);

  const onResize = debounce(() => {
    ctx.emit('tab-resize', buttonRef.value);
  }, 250);

  function onActivate() {
    ctx.emit('tab-active', buttonRef.value);
  }

  function onTabClick(event: MouseEvent) {
    event.stopPropagation();

    if (!active.value) {
      ctx.emit('tab-click', id, event);
    }
  }

  function onTabMouseDown(event: MouseEvent) {
    event.preventDefault();
    ctx.emit('mousedown');
  }

  watch(
    active,
    (isActive) => {
      if (isActive) {
        window.addEventListener('resize', onResize);
        onActivate();
      } else {
        window.removeEventListener('resize', onResize);
      }
    },
    { immediate: true },
  );

  onMounted(() => {
    if (active.value) {
      window.setTimeout(onActivate, 50);
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize);
  });

  return { buttonRef, onTabClick, onTabMouseDown };
}
